/**
 * Dashboard panoramica del modulo cassa.
 *
 * Mostra il turno corrente (se aperto), gli ultimi scontrini del giorno
 * e i totali per metodo di pagamento. Consente l'apertura turno e
 * l'avvio di una nuova vendita.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Clock, Receipt, ShoppingCart } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';

/** Headers comuni per le fetch autenticate. */
const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

interface TurnoInfo {
  id: string;
  stato: string;
  apertura_at: string;
  fondo_cassa_iniziale: string;
  operatore_id: string;
}

interface ScontrinoItem {
  id: string;
  year: number;
  tipo: string;
  stato: string;
  totale: string;
  created_at: string;
}

const CassaOverview: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useFormatters();
  const currentYear = new Date().getFullYear();

  const [turnoAperto, setTurnoAperto] = useState<TurnoInfo | null>(null);
  const [scontrini, setScontrini] = useState<ScontrinoItem[]>([]);
  const [loadingTurno, setLoadingTurno] = useState(true);
  const [loadingScontrini, setLoadingScontrini] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const caricaTurno = useCallback(async () => {
    setLoadingTurno(true);
    try {
      const res = await fetch('/api/v1/cassa/turni?stato=aperto&limit=1', {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: TurnoInfo[] = await res.json();
      setTurnoAperto(data.length > 0 ? data[0] : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoadingTurno(false);
    }
  }, []);

  const caricaScontrini = useCallback(async () => {
    setLoadingScontrini(true);
    try {
      const params = new URLSearchParams({
        year: String(currentYear),
        limit: '10',
      });
      const res = await fetch(`/api/v1/cassa/scontrini?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: ScontrinoItem[] = await res.json();
      setScontrini(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoadingScontrini(false);
    }
  }, [currentYear]);

  useEffect(() => {
    void caricaTurno();
    void caricaScontrini();
  }, [caricaTurno, caricaScontrini]);

  const formatDateTime = (iso: string): string => fmt.dateTime(iso);

  const formatCurrency = (value: string): string => fmt.currency(value);

  const statoVariant = (stato: string) => {
    switch (stato) {
      case 'chiuso':
        return 'secondary' as const;
      case 'annullato':
        return 'destructive' as const;
      default:
        return 'outline' as const;
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Cassa
        </h1>
        <div className="flex gap-3">
          {turnoAperto ? (
            <Button onClick={() => navigate('/cassa/vendita?nuovo=1')}>
              <ShoppingCart className="mr-2 h-4 w-4" />
              Nuova vendita
            </Button>
          ) : (
            <Button variant="outline" disabled={loadingTurno} onClick={() => navigate('/cassa/turni')}>
              Apri turno per iniziare
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Stato turno */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Turno corrente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTurno && (
            <p className="text-sm text-muted-foreground">Caricamento…</p>
          )}
          {!loadingTurno && !turnoAperto && (
            <p className="text-sm text-muted-foreground">
              Nessun turno aperto. Apri un turno dalla pagina Turni per iniziare a vendere.
            </p>
          )}
          {!loadingTurno && turnoAperto && (
            <div className="flex items-center gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Aperto alle</p>
                <p className="font-mono text-sm">{formatDateTime(turnoAperto.apertura_at)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fondo cassa</p>
                <p className="font-mono text-sm">{formatCurrency(turnoAperto.fondo_cassa_iniziale)}</p>
              </div>
              <Badge variant="outline">Aperto</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ultimi scontrini */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Ultimi scontrini
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingScontrini && (
            <p className="p-6 text-center text-sm text-muted-foreground">Caricamento…</p>
          )}
          {!loadingScontrini && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scontrini.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                      Nessuno scontrino registrato
                    </TableCell>
                  </TableRow>
                )}
                {scontrini.map((s: any) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/cassa/scontrini/${s.id}?year=${s.year}`)}
                  >
                    <TableCell>
                      <Badge variant="outline">{s.tipo}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statoVariant(s.stato)}>{s.stato}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(s.totale)}
                    </TableCell>
                    <TableCell className="text-sm">{formatDateTime(s.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CassaOverview;
